import { useQuery } from '@tanstack/react-query';
import { subscriptionService } from '../../../services/subscription';
import type { SubscriptionTransaction } from '../../../models/subscription';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';

interface OrganizationTransactionHistoryParams {
  organizationId?: string;
  page?: number;
  limit?: number;
  enabled?: boolean;
}

/**
 * Keyed by the current organization even when no id is passed.
 *
 * The request is scoped by the `X-Organization-ID` header, so without the id in the
 * key every organization shared one cache entry and the page showed the previously
 * opened organization's numbers for the whole staleTime. Same fix as useSubscription.
 */
export const useOrganizationTransactionHistory = ({
  organizationId: explicitOrganizationId,
  page = 1,
  limit = 10,
  enabled = true,
}: OrganizationTransactionHistoryParams = {}) => {
  const currentOrganizationId = useAppSelector(
    (state: RootState) => state.organization.organizationId,
  );
  const organizationId = explicitOrganizationId ?? currentOrganizationId;

  return useQuery<SubscriptionTransaction[], Error>({
    queryKey: ['organizationTransactionHistory', organizationId, page, limit],
    queryFn: () =>
      subscriptionService.getOrganizationTransactionHistory(
        explicitOrganizationId,
        page,
        limit,
      ),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: enabled && !!organizationId,
  });
};
